import { useState } from "react";
import { useRecoilState } from "recoil";
import { transportPriceClipboardAtom } from "../../../system/atoms/transportPriceClipboard";
import {
	Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Chip, IconButton, MenuItem, Table, TableBody, TableCell,
	TableHead, TableRow, TextField, Typography
} from "@mui/material";
import { Add, ContentCopy, ContentPaste, Delete, ExpandMore } from "@mui/icons-material";
import ask from "Dialogs/ask";
import { maximumNumberOfVehicles } from "./transportPriceLabels";
import {
	allTypeKeys, Band, CompoundOption, createBand, formatDestinations, OriginModel, ownTypeKey, parseDestinations,
	placeKey, placeLabel, Price, TypeKey, typeKeyLabel
} from "./transportPriceBands";

const quantities = Array.from({ length: maximumNumberOfVehicles }, (_, i) => i + 1);
const typeOrder = (t: TypeKey) => allTypeKeys.indexOf(t);

interface Props {
	originKey: string;
	origin: OriginModel;
	isOwn: boolean;
	editable: boolean;
	expanded: boolean;
	areaCodes: string[];
	compounds: CompoundOption[];
	onToggle: () => void;
	onChange: (origin: OriginModel) => void;
	onRemove: () => void;
}

const TransportPriceOriginSection = (props: Props) => {
	const { origin, isOwn, editable, areaCodes, compounds } = props;
	const types = Object.keys(origin.bands).sort((a, b) => typeOrder(a) - typeOrder(b));
	const [selected, setSelected] = useState<TypeKey>();
	const [clipboard, setClipboard] = useRecoilState(transportPriceClipboardAtom);
	const compoundNames = compounds.map(c => c.name);

	const selectedType = isOwn ? ownTypeKey : selected && types.includes(selected) ? selected : types[0];
	const bands = (selectedType && origin.bands[selectedType]) || [];
	const rowCount = Object.values(origin.bands).reduce((sum, b) => sum + b.length, 0);

	const setBands = (update: (bands: Band[]) => Band[]) => {
		if (!selectedType) {
			return;
		}

		props.onChange({ ...origin, bands: { ...origin.bands, [selectedType]: update(bands) } });
	};

	const updateBand = (bandId: string, changes: Partial<Band>) =>
		setBands(b => b.map(x => (x.id === bandId ? { ...x, ...changes } : x)));

	const setPrice = (band: Band, index: number, value: string) =>
		updateBand(band.id, { prices: band.prices.map((p, i) => (i === index ? (value === "" ? "" : Number(value)) as Price : p)) });

	const addType = (type: TypeKey) => {
		props.onChange({ ...origin, bands: { ...origin.bands, [type]: [] } });
		setSelected(type);
	};

	const removeType = async (type: TypeKey) => {
		if (origin.bands[type].length > 0) {
			const { yes } = await ask(`Alle Preise für ${typeKeyLabel(type)} ab ${placeLabel(origin.origin)} entfernen?`);
			if (!yes) {
				return;
			}
		}

		const { [type]: _removed, ...rest } = origin.bands;
		props.onChange({ ...origin, bands: rest });
	};

	const typeLabel = selectedType ? typeKeyLabel(selectedType) : "";
	const isCopySource = !!selectedType && clipboard?.sourceKey === props.originKey && clipboard.typeKey === selectedType;
	const canPaste = !!selectedType && !!clipboard && !isCopySource && clipboard.isOwn === isOwn;
	const clipboardName = clipboard ? (isOwn ? clipboard.sourceLabel : `${clipboard.sourceLabel} / ${typeKeyLabel(clipboard.typeKey)}`) : "";

	const copy = () => selectedType && setClipboard({
		sourceKey: props.originKey,
		typeKey: selectedType,
		sourceLabel: placeLabel(origin.origin),
		isOwn,
		bands: bands.map(x => ({ ...x, prices: [...x.prices] }))
	});

	const paste = async () => {
		if (!clipboard || !selectedType) {
			return;
		}

		if (bands.length > 0) {
			const target = isOwn ? placeLabel(origin.origin) : `${placeLabel(origin.origin)} / ${typeLabel}`;
			const { yes } = await ask(`Die vorhandenen Preise ab ${target} werden durch die Preise ab ${clipboardName} ersetzt. Fortfahren?`);
			if (!yes) {
				return;
			}
		}

		setBands(() => clipboard.bands.map(x => ({ ...createBand(), destinations: x.destinations, prices: [...x.prices] })));
	};

	// The last comma separated token is the one being completed
	const lastToken = (text: string) => text.substring(text.lastIndexOf(",") + 1).trim();

	// Areas that no band of the selected type covers, to spot gaps quickly
	const covered = new Set(bands.flatMap(b => parseDestinations(b.destinations, areaCodes, compounds).places.map(placeKey)));
	const uncovered = formatDestinations(
		areaCodes.filter(a => !covered.has(`Area:${a}`) && `Area:${a}` !== props.originKey).map(key => ({ kind: "Area" as const, key })),
		areaCodes
	);

	const missingTypes = allTypeKeys.filter(t => !types.includes(t));

	return (
		<Accordion sx={{ minWidth: 0 }} expanded={props.expanded} onChange={props.onToggle} disableGutters TransitionProps={{ unmountOnExit: true }}>
			<AccordionSummary expandIcon={<ExpandMore />}>
				<Box display="flex" alignItems="center" gap={2} width="100%">
					<Typography variant="subtitle1" fontWeight={500}>{placeLabel(origin.origin)}</Typography>
					<Typography variant="body2" color="text.secondary">{rowCount} Preiszeilen</Typography>
					<Box flexGrow={1} />
					{editable && (
						<>
							{canPaste && (
								<IconButton size="small" title={`Preise ab ${clipboardName} einfügen`} onClick={e => { e.stopPropagation(); paste(); }}>
									<ContentPaste fontSize="small" />
								</IconButton>
							)}
							<IconButton size="small" color={isCopySource ? "primary" : "default"} title={selectedType && !isOwn ? `Preise ${typeLabel} kopieren` : "Preise kopieren"} onClick={e => { e.stopPropagation(); copy(); }}>
								<ContentCopy fontSize="small" />
							</IconButton>
							<IconButton size="small" title="Ausgangsort entfernen" onClick={e => { e.stopPropagation(); props.onRemove(); }}>
								<Delete fontSize="small" />
							</IconButton>
						</>
					)}
				</Box>
			</AccordionSummary>
			<AccordionDetails>
				{!isOwn && (
					<Box mb={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
						{editable && (
							<TextField variant="standard" select size="small" value="" label="Klasse hinzufügen" sx={{ minWidth: 158 }}
								disabled={missingTypes.length === 0} onChange={e => addType(e.target.value)}>
								{missingTypes.map(t => <MenuItem key={t} value={t}>{typeKeyLabel(t)}</MenuItem>)}
							</TextField>
						)}
						{types.map(t => (
							<Chip
								key={t}
								label={typeKeyLabel(t)}
								color={t === selectedType ? "primary" : "default"}
								onClick={() => setSelected(t)}
								onDelete={editable ? () => removeType(t) : undefined}
							/>
						))}
					</Box>
				)}
				{selectedType && (
					<>
						<Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ minWidth: 220 }}>Ziele (PLZ-Gebiete / Compounds)</TableCell>
									{isOwn
										? <TableCell>Preis je Fahrzeug (€)</TableCell>
										: quantities.map(q => <TableCell key={q}>{q} Fz.</TableCell>)}
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{bands.map(band => {
									const parsed = parseDestinations(band.destinations, areaCodes, compounds);

									return (
										<TableRow key={band.id}>
											<TableCell>
												<Autocomplete
													freeSolo
													fullWidth
													openOnFocus
													disabled={!editable}
													value={null}
													options={compoundNames}
													inputValue={band.destinations}
													filterOptions={(options, state) => {
														const token = lastToken(state.inputValue).toLowerCase();
														return options.filter(o => o.toLowerCase().includes(token));
													}}
													onInputChange={(_, value, reason) => (reason === "input" || reason === "clear") && updateBand(band.id, { destinations: value })}
													onChange={(_, value, reason) => {
														if (reason === "selectOption" && typeof value === "string") {
															const text = band.destinations;
															const prefix = text.substring(0, text.lastIndexOf(",") + 1);
															updateBand(band.id, { destinations: `${prefix}${prefix ? " " : ""}${value}` });
														}
													}}
													renderInput={params => (
														<TextField {...params} variant="standard" placeholder="z. B. 01-09, 12, 14-19"
															error={parsed.errors.length > 0}
															helperText={parsed.errors[0] ?? (parsed.places.length ? `${parsed.places.length} Ziele` : undefined)} />
													)}
												/>
											</TableCell>
											{(isOwn ? [0] : quantities.map(q => q - 1)).map(index => (
												<TableCell key={index}>
													<TextField variant="standard" type="number" size="small" disabled={!editable}
														sx={{ width: isOwn ? 120 : 80 }} inputProps={{ step: 0.01, min: 0 }}
														value={band.prices[index]} onChange={e => setPrice(band, index, e.target.value)} />
												</TableCell>
											))}
											<TableCell>
												{editable && (
													<IconButton size="small" onClick={() => setBands(b => b.filter(x => x.id !== band.id))}><Delete fontSize="small" /></IconButton>
												)}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
						</Box>
						{editable && (
							<Box mt={2}>
								<Chip clickable variant="outlined" icon={<Add />} label="Preiszeile hinzufügen" onClick={() => setBands(b => [...b, createBand()])} />
							</Box>
						)}
						{bands.length > 0 && uncovered && (
							<Typography mt={2} variant="body2" color="text.secondary">Ohne Preis in dieser Tabelle: {uncovered}</Typography>
						)}
					</>
				)}
				{!selectedType && <Typography color="text.secondary">Fügen Sie eine Fahrzeugklasse oder Volumenkategorie hinzu.</Typography>}
			</AccordionDetails>
		</Accordion>
	);
};

export default TransportPriceOriginSection;
